import type { MassUpdateResultSummary } from "~produce/production/helpers/resolveMassUpdateSelection";
import type { MassUpdateVariationRow } from "~produce/production/recipes/helpers/massUpdateVariationsGrid";
import type { RecipeTargetForm } from "~produce/production/recipes/helpers/recipeTarget";
import type { VariationActiveJobs } from "~produce/production/recipes/helpers/variationActiveJobs";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import ViewType from "~/@types/viewType";
import {
	ProductionRecipeDocument,
	ProductionRecipeVariationData,
	ProductionRecipeVariationDataInput,
	ProductionRecipeVariationDocument
} from "~/gql/graphqlBase.generated";
import { type ProductionRecipeVariationSummaryFragment } from "~/gql/queries/produce/productionRecipes.generated";

export interface MassUpdateValues {
	growWeeks: string;
	plantsPerPot: string;
	yield: string;
}

export type MassUpdateEventEntityType = "Material" | "Space" | "Task";

export type MassUpdateEventOperation = "Add" | "Remove";

export interface MassUpdateEventsValues {
	eventIds: string[];
	eventType: MassUpdateEventEntityType | null;
	operation: MassUpdateEventOperation;
}

export interface MassUpdateSelectAllScope {
	excludedIds: string[];
	filterModel: Record<string, unknown>;
	searchText: string;
}

export interface MassUpdateWeekRangeFilter {
	endWeek: string;
	includeUnscheduled: boolean;
	startWeek: string;
}

export interface MassUpdateReviewRow {
	baseline: ProductionRecipeVariationDataInput;
	id: string;
	label: string;
	productionRecipeId: string;
	productionRecipeVariationId: string;
}

export interface MassUpdateUnconfirmedRow {
	attempted: ProductionRecipeVariationDataInput;
	id: string;
}

export interface MassUpdateReview {
	eventsValues: MassUpdateEventsValues;
	rows: MassUpdateReviewRow[];
	selectedCount: number;
	unconfirmed: MassUpdateUnconfirmedRow[];
	unscheduledCount: number;
	values: MassUpdateValues;
}

export interface MassUpdateWizard {
	activeStep: number;
	eventsValues: MassUpdateEventsValues;
	result: MassUpdateResultSummary | null;
	review: MassUpdateReview | null;
	selectAllScope: MassUpdateSelectAllScope | null;
	selectedVariations: MassUpdateVariationRow[];
	unconfirmed: MassUpdateUnconfirmedRow[];
	values: MassUpdateValues;
	weekRangeFilter: MassUpdateWeekRangeFilter;
}

export interface RecipeJobsTarget {
	beforeJobIds?: string[];
	productionRecipeId: string;
	variationId: null | string;
}

export interface VariationJobsPromptRequest {
	revision: number;
	targets: RecipeJobsTarget[];
}

export interface RecipeBrowsingClaim {
	epoch: number;
	owner: string;
}

interface RecipesState {
	createRecipeInput: null | RecipeTargetForm;
	currentRecipe: ProductionRecipeDocument | null;
	currentVariant: ProductionRecipeVariationData | ProductionRecipeVariationDocument | null;
	editCostsDrawerOpen: boolean;
	editRecipeDrawerOpen: boolean;
	exactSelectionOwner: RecipeBrowsingClaim | null;
	inspectionMode: boolean;
	massUpdateActiveStep: number;
	massUpdateEventsValues: MassUpdateEventsValues;
	massUpdateModalOpen: boolean;
	massUpdateResult: MassUpdateResultSummary | null;
	massUpdateReview: MassUpdateReview | null;
	massUpdateSelectAllScope: MassUpdateSelectAllScope | null;
	massUpdateSelectedVariations: MassUpdateVariationRow[];
	massUpdateUnconfirmed: MassUpdateUnconfirmedRow[];
	massUpdateValues: MassUpdateValues;
	massUpdateWeekRangeFilter: MassUpdateWeekRangeFilter;
	newRecipeDrawerOpen: boolean;
	pendingRecipeSelection: string | null;
	recipeModalOpen: boolean;
	revision: number;
	selectedFilterVariant:
		| ProductionRecipeVariationData
		| ProductionRecipeVariationDocument
		| ProductionRecipeVariationSummaryFragment
		| null;
	variationJobsOffer: VariationActiveJobs | null;
	variationJobsPromptRequest: null | VariationJobsPromptRequest;
	viewType: ViewType;
}

const emptyMassUpdateValues: MassUpdateValues = {
	growWeeks: "",
	plantsPerPot: "",
	yield: ""
};

const emptyMassUpdateEventsValues: MassUpdateEventsValues = {
	eventIds: [],
	eventType: null,
	operation: "Add"
};

const emptyMassUpdateWeekRangeFilter: MassUpdateWeekRangeFilter = {
	endWeek: "",
	includeUnscheduled: false,
	startWeek: ""
};

const initialState: RecipesState = {
	createRecipeInput: null,
	currentRecipe: null,
	currentVariant: null,
	editCostsDrawerOpen: false,
	editRecipeDrawerOpen: false,
	exactSelectionOwner: null,
	inspectionMode: false,
	massUpdateActiveStep: 0,
	massUpdateEventsValues: emptyMassUpdateEventsValues,
	massUpdateModalOpen: false,
	massUpdateResult: null,
	massUpdateReview: null,
	massUpdateSelectAllScope: null,
	massUpdateSelectedVariations: [],
	massUpdateUnconfirmed: [],
	massUpdateValues: emptyMassUpdateValues,
	massUpdateWeekRangeFilter: emptyMassUpdateWeekRangeFilter,
	newRecipeDrawerOpen: false,
	pendingRecipeSelection: null,
	recipeModalOpen: false,
	revision: 0,
	selectedFilterVariant: null,
	variationJobsOffer: null,
	variationJobsPromptRequest: null,
	viewType: "card"
};

export const captureMassUpdateWizard = (state: RecipesState): MassUpdateWizard => ({
	activeStep: state.massUpdateActiveStep,
	eventsValues: state.massUpdateEventsValues,
	result: state.massUpdateResult,
	review: state.massUpdateReview,
	selectAllScope: state.massUpdateSelectAllScope,
	selectedVariations: state.massUpdateSelectedVariations,
	unconfirmed: state.massUpdateUnconfirmed,
	values: state.massUpdateValues,
	weekRangeFilter: state.massUpdateWeekRangeFilter
});

const isSameMassUpdateValues = (current: MassUpdateValues, next: MassUpdateValues): boolean =>
	current.growWeeks === next.growWeeks && current.plantsPerPot === next.plantsPerPot && current.yield === next.yield;

const ownsExactSelection = (state: RecipesState, claim: RecipeBrowsingClaim): boolean =>
	state.exactSelectionOwner?.owner === claim.owner && state.exactSelectionOwner.epoch === claim.epoch;

const isHeldByALaterLease = (state: RecipesState, claim: RecipeBrowsingClaim): boolean =>
	state.exactSelectionOwner !== null && state.exactSelectionOwner.epoch > claim.epoch;

const recipesSlice = createSlice({
	initialState,
	name: "recipes",
	reducers: {
		clearCurrentVariant: (state) => {
			state.currentVariant = null;
		},
		clearVariationJobsOffer: (state) => {
			state.variationJobsOffer = null;
		},
		clearExactRecipeSelection: (state, action: PayloadAction<RecipeBrowsingClaim>) => {
			if (state.recipeModalOpen || state.massUpdateModalOpen) return;
			if (isHeldByALaterLease(state, action.payload)) return;
			state.currentRecipe = null;
			state.currentVariant = null;
			state.selectedFilterVariant = null;
			state.exactSelectionOwner = action.payload;
			state.inspectionMode = true;
		},
		closeEditCostsDrawer: (state) => {
			state.editCostsDrawerOpen = false;
		},
		closeEditRecipeDrawer: (state) => {
			state.editRecipeDrawerOpen = false;
		},
		closeMassUpdateModal: (state) => {
			state.massUpdateModalOpen = false;
			state.massUpdateActiveStep = 0;
			state.massUpdateSelectedVariations = [];
			state.massUpdateSelectAllScope = null;
			state.massUpdateValues = emptyMassUpdateValues;
			state.massUpdateEventsValues = emptyMassUpdateEventsValues;
			state.massUpdateWeekRangeFilter = emptyMassUpdateWeekRangeFilter;
			state.massUpdateReview = null;
			state.massUpdateResult = null;
			state.massUpdateUnconfirmed = [];
		},
		closeNewRecipeDrawer: (state) => {
			state.newRecipeDrawerOpen = false;
		},
		closeRecipeModal: (state) => {
			state.recipeModalOpen = false;
		},
		keepMassUpdateRowsForRetry: (
			state,
			action: PayloadAction<{ retryIds: string[]; unconfirmed: MassUpdateUnconfirmedRow[] }>
		) => {
			const retryIds = new Set(action.payload.retryIds);
			state.massUpdateSelectedVariations = state.massUpdateSelectedVariations.filter((row) => retryIds.has(row.id));
			state.massUpdateUnconfirmed = action.payload.unconfirmed;
			state.massUpdateSelectAllScope = null;
			state.massUpdateResult = null;
		},
		openEditCostsDrawer: (state) => {
			state.editCostsDrawerOpen = true;
		},
		openEditRecipeDrawer: (state) => {
			state.editRecipeDrawerOpen = true;
		},
		openMassUpdateModal: (state) => {
			state.massUpdateModalOpen = true;
		},
		openNewRecipeDrawer: (state) => {
			state.newRecipeDrawerOpen = true;
		},
		openRecipeModal: (state) => {
			state.recipeModalOpen = true;
		},
		releaseExactRecipeSelection: (state, action: PayloadAction<RecipeBrowsingClaim>) => {
			if (!ownsExactSelection(state, action.payload)) return;
			state.exactSelectionOwner = null;
			state.inspectionMode = false;
		},
		restoreMassUpdate: (state, action: PayloadAction<MassUpdateWizard>) => {
			state.massUpdateModalOpen = true;
			state.massUpdateActiveStep = action.payload.activeStep;
			state.massUpdateEventsValues = action.payload.eventsValues;
			state.massUpdateResult = action.payload.result;
			state.massUpdateReview = action.payload.review;
			state.massUpdateSelectAllScope = action.payload.selectAllScope;
			state.massUpdateSelectedVariations = action.payload.selectedVariations;
			state.massUpdateUnconfirmed = action.payload.unconfirmed;
			state.massUpdateValues = action.payload.values;
			state.massUpdateWeekRangeFilter = action.payload.weekRangeFilter;
		},
		setCreateRecipeInput: (state, action: PayloadAction<null | RecipeTargetForm>) => {
			state.createRecipeInput = action.payload;
		},
		setCurrentRecipe: (state, action: PayloadAction<ProductionRecipeDocument | null>) => {
			state.currentRecipe = action.payload;
			state.exactSelectionOwner = null;
			state.inspectionMode = false;
		},
		setExactRecipeSelection: (
			state,
			action: PayloadAction<{ claim: RecipeBrowsingClaim; recipe: ProductionRecipeDocument }>
		) => {
			if (state.recipeModalOpen || state.massUpdateModalOpen) return;
			if (isHeldByALaterLease(state, action.payload.claim)) return;
			if (
				ownsExactSelection(state, action.payload.claim) &&
				state.currentRecipe?.productionRecipeId === action.payload.recipe.productionRecipeId
			)
				return;
			state.currentRecipe = action.payload.recipe;
			state.currentVariant = null;
			state.selectedFilterVariant = null;
			state.createRecipeInput = null;
			state.exactSelectionOwner = action.payload.claim;
			state.inspectionMode = true;
		},
		setCurrentVariant: (
			state,
			action: PayloadAction<ProductionRecipeVariationData | ProductionRecipeVariationDocument | null>
		) => {
			state.currentVariant = action.payload;
		},
		setMassUpdateActiveStep: (state, action: PayloadAction<number>) => {
			state.massUpdateActiveStep = action.payload;
		},
		setMassUpdateEventsValues: (state, action: PayloadAction<MassUpdateEventsValues>) => {
			state.massUpdateEventsValues = action.payload;
			state.massUpdateUnconfirmed = [];
		},
		setMassUpdateResult: (state, action: PayloadAction<MassUpdateResultSummary | null>) => {
			state.massUpdateResult = action.payload;
		},
		setMassUpdateReview: (state, action: PayloadAction<MassUpdateReview | null>) => {
			state.massUpdateReview = action.payload;
		},
		setMassUpdateSelectAllScope: (state, action: PayloadAction<MassUpdateSelectAllScope | null>) => {
			state.massUpdateSelectAllScope = action.payload;
		},
		setMassUpdateSelectedVariations: (state, action: PayloadAction<MassUpdateVariationRow[]>) => {
			state.massUpdateSelectedVariations = action.payload;
		},
		setMassUpdateValues: (state, action: PayloadAction<MassUpdateValues>) => {
			if (!isSameMassUpdateValues(state.massUpdateValues, action.payload)) state.massUpdateUnconfirmed = [];
			state.massUpdateValues = action.payload;
		},
		setMassUpdateWeekRangeFilter: (state, action: PayloadAction<MassUpdateWeekRangeFilter>) => {
			state.massUpdateWeekRangeFilter = action.payload;
		},
		setPendingRecipeSelection: (state, action: PayloadAction<string | null>) => {
			state.pendingRecipeSelection = action.payload;
		},
		setSelectedFilterVariant: (
			state,
			action: PayloadAction<
				| ProductionRecipeVariationData
				| ProductionRecipeVariationDocument
				| ProductionRecipeVariationSummaryFragment
				| null
			>
		) => {
			state.selectedFilterVariant = action.payload;
		},
		setViewType: (state, action: PayloadAction<ViewType>) => {
			state.viewType = action.payload;
		},
		updateCurrentRecipe: (
			state,
			action: PayloadAction<Partial<ProductionRecipeDocument> & { productionRecipeId: string }>
		) => {
			if (!state.currentRecipe) return;
			if (state.currentRecipe.productionRecipeId !== action.payload.productionRecipeId) return;
			state.currentRecipe = { ...state.currentRecipe, ...action.payload };
		},
		resolveVariationJobsLookup: (
			state,
			action: PayloadAction<{ offer: VariationActiveJobs | null; revision: number }>
		) => {
			if (state.variationJobsPromptRequest?.revision !== action.payload.revision) return;
			state.variationJobsPromptRequest = null;
			if (action.payload.offer) state.variationJobsOffer = action.payload.offer;
		},
		setVariationJobsPromptRequest: (state, action: PayloadAction<RecipeJobsTarget[]>) => {
			const merged = new Map<string, RecipeJobsTarget>();
			for (const target of [...(state.variationJobsPromptRequest?.targets ?? []), ...action.payload]) {
				const key = `${target.productionRecipeId}:${target.variationId ?? ""}`;
				const beforeJobIds = [...new Set([...(merged.get(key)?.beforeJobIds ?? []), ...(target.beforeJobIds ?? [])])];
				merged.set(key, { ...target, beforeJobIds });
			}
			state.variationJobsPromptRequest = {
				revision: (state.variationJobsPromptRequest?.revision ?? 0) + 1,
				targets: [...merged.values()]
			};
		},
		updateRevision: (state) => {
			state.revision += 1;
		}
	}
});

export const {
	clearCurrentVariant,
	clearExactRecipeSelection,
	clearVariationJobsOffer,
	closeEditCostsDrawer,
	closeEditRecipeDrawer,
	closeMassUpdateModal,
	closeNewRecipeDrawer,
	closeRecipeModal,
	keepMassUpdateRowsForRetry,
	openEditCostsDrawer,
	openEditRecipeDrawer,
	openMassUpdateModal,
	openNewRecipeDrawer,
	openRecipeModal,
	releaseExactRecipeSelection,
	resolveVariationJobsLookup,
	restoreMassUpdate,
	setCreateRecipeInput,
	setCurrentRecipe,
	setCurrentVariant,
	setExactRecipeSelection,
	setMassUpdateActiveStep,
	setMassUpdateEventsValues,
	setMassUpdateResult,
	setMassUpdateReview,
	setMassUpdateSelectAllScope,
	setMassUpdateSelectedVariations,
	setMassUpdateValues,
	setVariationJobsPromptRequest,
	setMassUpdateWeekRangeFilter,
	setPendingRecipeSelection,
	setSelectedFilterVariant,
	setViewType,
	updateCurrentRecipe,
	updateRevision
} = recipesSlice.actions;

export default recipesSlice.reducer;
